import { type EnterpriseCustomWorkflow } from "@mg/schemas/src/commons/enterprise";
import {
  ClipboardText,
  FileMagnifyingGlass,
  Lightbulb,
  PaintBrush,
  ShieldCheck,
  Wrench,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";

import { WORKFLOW_CARDS } from "../mockData";

interface SkillCardsProps {
  onCardClick: (workflowType: string) => void;
  enabledSkillIds?: string[];
  customWorkflows?: EnterpriseCustomWorkflow[];
}

const WORKFLOW_ICONS: Record<string, React.ReactNode> = {
  artwork_review: (
    <PaintBrush size={22} className="text-puntt-pink-9" weight="duotone" />
  ),
  brief_comparison: (
    <FileMagnifyingGlass
      size={22}
      className="text-puntt-accent-9"
      weight="duotone"
    />
  ),
  brief_creation: (
    <ClipboardText size={22} className="text-puntt-accent-9" weight="duotone" />
  ),
  compliance_review: (
    <ShieldCheck size={22} className="text-malachite-700" weight="duotone" />
  ),
  claim_generation: (
    <Lightbulb size={22} className="text-puntt-gold-9" weight="duotone" />
  ),
};

const WORKFLOW_ICON_BG: Record<string, string> = {
  artwork_review: "bg-puntt-pink-2 ring-1 ring-puntt-pink-5/50",
  brief_comparison: "bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50",
  brief_creation: "bg-puntt-accent-2 ring-1 ring-puntt-accent-5/50",
  compliance_review: "bg-malachite-50 ring-1 ring-malachite-200/50",
  claim_generation: "bg-puntt-gold-2 ring-1 ring-puntt-gold-5/50",
};

/** @deprecated Use SkillCards instead */
export function WorkflowCards({
  onCardClick,
  enabledSkillIds,
  customWorkflows,
}: SkillCardsProps) {
  return (
    <SkillCards
      onCardClick={onCardClick}
      enabledSkillIds={enabledSkillIds}
      customWorkflows={customWorkflows}
    />
  );
}

export function SkillCards({
  onCardClick,
  enabledSkillIds,
  customWorkflows,
}: SkillCardsProps) {
  const filteredCards = enabledSkillIds
    ? WORKFLOW_CARDS.filter((card) =>
        enabledSkillIds.includes(card.workflowType),
      )
    : WORKFLOW_CARDS;

  const enabledCustomWorkflows = (customWorkflows ?? []).filter(
    (wf) => wf.enabled,
  );

  return (
    <div className="grid grid-cols-4 gap-3">
      {filteredCards.map((card) => (
        <button
          key={card.id}
          data-testid={`ask-puntt-skill-card-${card.workflowType}`}
          onClick={() => onCardClick(card.id)}
          className="group flex flex-col gap-2 rounded-2xl border border-puntt-neutral-gray-5 bg-base-white p-5 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-puntt-accent-7 hover:shadow-lg"
        >
          <div
            className={`flex size-10 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-105 group-hover:ring-2 ${WORKFLOW_ICON_BG[card.id] ?? WORKFLOW_ICON_BG[card.workflowType] ?? "bg-puntt-neutral-gray-2"}`}
          >
            {WORKFLOW_ICONS[card.id] ?? WORKFLOW_ICONS[card.workflowType]}
          </div>
          <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
            {card.title}
          </Text>
          <Text size="1" className="leading-relaxed text-puntt-neutral-gray-11">
            {card.description}
          </Text>
        </button>
      ))}
      {enabledCustomWorkflows.map((wf) => (
        <button
          key={wf.workflowId}
          data-testid={`ask-puntt-skill-card-custom_${wf.workflowId}`}
          onClick={() => onCardClick(`custom_${wf.workflowId}`)}
          className="group flex flex-col gap-2 rounded-2xl border border-puntt-neutral-gray-5 bg-base-white p-5 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-puntt-accent-7 hover:shadow-lg"
        >
          <div className="flex size-10 items-center justify-center rounded-xl bg-puntt-neutral-gray-3 ring-1 ring-puntt-neutral-gray-5/50 transition-all duration-300 group-hover:scale-105 group-hover:ring-2">
            <Wrench
              size={22}
              className="text-puntt-neutral-gray-11"
              weight="duotone"
            />
          </div>
          <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
            {wf.name}
          </Text>
          <Text
            size="1"
            className="line-clamp-2 leading-relaxed text-puntt-neutral-gray-11"
          >
            {wf.trigger}
          </Text>
        </button>
      ))}
    </div>
  );
}
